// src/components/Alerts.jsx
//
// The owner's "Alertes" page for conciergerie mode
// (documentations/conciergerie-plan.md): incidents opened from guest problem
// reports, most urgent first, with start / resolve / close actions.
// Backed by backend/routes/incidents.ts, which scopes everything to the
// facilities the logged-in account owns.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Select, MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { initKeycloak } from '../keycloak';
import ConciergerieStats from './conciergerie/ConciergerieStats';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const PRIORITY_COLOR = { URGENT: 'error', HIGH: 'warning', NORMAL: 'info', LOW: 'default' };

export default function Alerts() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [tab, setTab] = useState('open');
  const [incidents, setIncidents] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [resolving, setResolving] = useState(null); // incident being resolved (dialog)
  const [comment, setComment] = useState('');
  const [team, setTeam] = useState([]); // active members, to assign to (backend/routes/staff.ts)

  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      if (!kc.authenticated) return kc.login();
      setToken(kc.token ?? '');
    })().catch(console.error);
  }, []);

  const load = useCallback(() => {
    if (!token) return;
    setIncidents(null);
    setError('');
    fetch(`${API_BASE_URL}/v2/incidents?status=${tab}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => setIncidents(data.incidents || []))
      .catch(() => setError(t('alerts.loadError')));
  }, [token, tab, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/v2/staff`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { staff: [] }))
      .then((d) => setTeam((d.staff || []).filter((m) => m.active)))
      .catch(() => {}); // assigning is optional; the page works without it
  }, [token]);

  const act = async (incident, action, text, staffId) => {
    setBusyId(incident.id);
    setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/v2/incidents/${incident.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, comment: text, staffId }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      load();
    } catch {
      setError(t('alerts.actionError'));
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : '');

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button variant="text" onClick={() => navigate('/showDashboard')} sx={{ mb: 1 }}>
        ← {t('alerts.back')}
      </Button>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4">{t('alerts.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="text" onClick={() => navigate('/alerts/guide')}>{t('alerts.guide')}</Button>
          <Button variant="outlined" onClick={() => navigate('/team')}>{t('alerts.team')}</Button>
        </Stack>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>{t('alerts.intro')}</Typography>

      <ConciergerieStats token={token} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="open" label={t('alerts.tabOpen')} />
        <Tab value="done" label={t('alerts.tabDone')} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {incidents === null && !error && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
      {incidents && incidents.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {tab === 'open' ? t('alerts.emptyOpen') : t('alerts.emptyDone')}
        </Typography>
      )}

      <Stack spacing={2}>
        {incidents && incidents.map((i) => (
          <Paper key={i.id} variant="outlined" sx={{ p: 2, borderLeft: 6, borderLeftColor: `${PRIORITY_COLOR[i.priority] === 'default' ? 'grey' : PRIORITY_COLOR[i.priority]}.main` }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip size="small" color={PRIORITY_COLOR[i.priority]} label={t(`alerts.priority.${i.priority}`)} />
              <Typography sx={{ fontWeight: 700 }}>{t(`alerts.category.${i.category}`)}</Typography>
              <Typography color="text.secondary">— {i.facility_name}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Chip size="small" variant="outlined" label={t(`alerts.status.${i.status}`)} />
            </Stack>

            {i.comment && <Typography sx={{ mb: 1 }}>« {i.comment} »</Typography>}
            <Typography variant="body2" color="text.secondary">
              {t('alerts.reportedAt', { date: fmtDate(i.created_at) })}
              {i.rating != null && ` · ${t('alerts.rating', { rating: Number(i.rating) })}`}
              {i.presence_status === 'OUT_OF_RANGE' && ` · ${t('alerts.outOfRange')}`}
              {i.assigned_name && ` · ${t('staffPortal.assignedTo', { name: i.assigned_name })}`}
            </Typography>
            {i.resolved_at && (
              <Typography variant="body2" color="text.secondary">
                {t('alerts.resolvedAt', { date: fmtDate(i.resolved_at) })}
                {i.resolution_comment && ` — ${i.resolution_comment}`}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {team.length > 0 && ['OPEN', 'ASSIGNED', 'IN_PROGRESS'].includes(i.status) && (
                <Select size="small" displayEmpty value={i.assigned_staff_id ?? ''} disabled={busyId === i.id}
                  onChange={(e) => act(i, 'assign', undefined, Number(e.target.value))}>
                  <MenuItem value="" disabled>{t('staffPortal.assign')}</MenuItem>
                  {team.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </Select>
              )}
              {['OPEN', 'ASSIGNED'].includes(i.status) && (
                <Button size="small" variant="outlined" disabled={busyId === i.id} onClick={() => act(i, 'start')}>
                  {t('alerts.start')}
                </Button>
              )}
              {['OPEN', 'ASSIGNED', 'IN_PROGRESS'].includes(i.status) && (
                <Button size="small" variant="contained" color="success" disabled={busyId === i.id}
                  onClick={() => { setResolving(i); setComment(''); }}>
                  {t('alerts.resolve')}
                </Button>
              )}
              {i.status === 'RESOLVED' && (
                <Button size="small" variant="outlined" disabled={busyId === i.id} onClick={() => act(i, 'close')}>
                  {t('alerts.close')}
                </Button>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog open={Boolean(resolving)} onClose={() => setResolving(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('alerts.resolveTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth multiline minRows={2} sx={{ mt: 1 }}
            label={t('alerts.resolveComment')}
            value={comment}
            inputProps={{ maxLength: 1000 }}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolving(null)}>{t('alerts.cancel')}</Button>
          <Button variant="contained" color="success"
            onClick={() => { const i = resolving; setResolving(null); act(i, 'resolve', comment); }}>
            {t('alerts.resolve')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
